import { useEffect, useState } from "react";
import {
  CATEGORIAS,
  INTEGRADORES,
  confirmarLinks,
  definirModeloAtivo,
  lerEstadosParaAdmin,
  lerModelosParaAdmin,
  renomearModelo,
  rotuloDaCategoria,
  rotuloDoIntegrador,
  salvarEstado,
  type EstadoParaAdmin,
  type Integrador,
  type ModeloDeContrato,
} from "../lib/legaliza";
import { CAMPO, Recado } from "./ui";
import { SeloDeIntegrador } from "./LegalizaUi";

/**
 * Admin → Legalização: as 27 juntas comerciais e os modelos de contrato.
 *
 * ## Por que esta tela existe
 *
 * No app de origem os 27 estados eram uma constante de 366 linhas com 81 URLs de
 * portal de governo dentro. Portal de junta muda de endereço sem lei nova e sem
 * aviso — e já estava acontecendo: o Sergipe só funcionava por redirect, e a
 * JUCESP servia certificado quebrado (medido em 25/08). Corrigir exigia deploy, e
 * quem descobria era o aluno clicando em "Abrir empresa".
 *
 * Aqui o catálogo é dado (000115), e esta tela é onde a equipe de conteúdo abre o
 * site da junta, confere os três endereços e diz "conferi".
 *
 * ## A trilha de conferência, e por que ela morre sozinha
 *
 * "Salvar e confirmar" grava os links E carimba quem e quando — pelo servidor
 * (RPC `legaliza_confirmar_links`), porque o relógio do navegador do operador pode
 * estar em qualquer data. E o carimbo é uma afirmação sobre TRÊS ENDEREÇOS: se um
 * deles mudar depois, por qualquer caminho, o gatilho da 000115 zera o carimbo.
 * Selo verde sobre link que ninguém viu é pior do que nenhum selo.
 *
 * ## O texto da planilha é PISTA, não decisão
 *
 * `nota_de_origem` ('NÃO FORNECE', 'MANUAL', ou a descrição do que a junta
 * publicava) aparece ao lado de cada estado para a equipe conferir se o catálogo
 * de modelos daquele estado está completo. No app de origem esse texto decidia
 * frases da tela — e dois dos três ramos eram inalcançáveis. Aqui ele não decide
 * nada; ele aponta onde olhar. O Espírito Santo é o caso que motivou isto: a
 * planilha dizia que a JUCEES fornecia modelos, e o catálogo não tinha nenhum.
 *
 * ## O que esta tela NÃO decide
 *
 * Nada. Quem pode escrever é `pode_editar('legalizacao')` (000297) — na policy das duas tabelas e dentro
 * da RPC. Quem valida o link (https, sem `javascript:`) é a constraint. A tela
 * mostra a recusa do banco com as palavras do banco.
 */
export default function AdminLegaliza() {
  const [estados, setEstados] = useState<EstadoParaAdmin[] | null>(null);
  const [modelos, setModelos] = useState<ModeloDeContrato[] | null>(null);
  const [erro, setErro] = useState(false);
  const [recado, setRecado] = useState<{ tipo: "ok" | "erro"; texto: string } | null>(null);
  const [soPorConferir, setSoPorConferir] = useState(false);

  const recarregar = async () => {
    const [e, m] = await Promise.all([lerEstadosParaAdmin(), lerModelosParaAdmin()]);
    if (!e || !m) {
      setErro(true);
      return;
    }
    setErro(false);
    setEstados(e);
    setModelos(m);
  };

  useEffect(() => {
    void recarregar();
  }, []);

  const aviso = (tipo: "ok" | "erro", texto: string) => {
    setRecado({ tipo, texto });
    // O recado de acerto some sozinho; o de erro fica até a próxima ação, porque
    // quem errou precisa poder LER a recusa do banco com calma.
    if (tipo === "ok") window.setTimeout(() => setRecado(null), 4000);
  };

  if (erro) {
    return (
      <Recado
        tipo="erro"
        texto="Não foi possível ler o catálogo das juntas. Recarregue a página; se continuar, o banco pode estar fora do ar."
      />
    );
  }
  if (!estados || !modelos) return <p className="text-sm text-cinza">Carregando o catálogo…</p>;

  const conferidos = estados.filter((e) => e.links_conferidos_em).length;
  const porConferir = estados.length - conferidos;
  const visiveis = soPorConferir ? estados.filter((e) => !e.links_conferidos_em) : estados;

  return (
    <div className="space-y-8">
      <header>
        <h2 className="font-display text-xl font-bold text-titulo">Legalização — as juntas comerciais</h2>
        <p className="mt-1 max-w-2xl text-sm leading-relaxed text-cinza">
          Os endereços oficiais de abertura, alteração e baixa de cada estado, e os modelos de
          contrato que a ferramenta entrega. Toda alteração vale <strong>na hora</strong> para
          os alunos. Para conferir um estado: abra os três links, veja se cada um leva ao lugar
          certo, corrija o que mudou e clique em <strong>Salvar e confirmar links</strong>.
        </p>
        <p
          id="resumo-conferencia"
          data-conferidos={conferidos}
          data-por-conferir={porConferir}
          className={`mt-3 inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-sm font-semibold ${
            porConferir === 0 ? "bg-aviso-fundo text-aviso-tinta" : "bg-alerta-fundo text-alerta-tinta"
          }`}
        >
          {conferidos} de {estados.length} com os links conferidos
          {porConferir > 0 && <span className="font-normal">· {porConferir} por conferir</span>}
        </p>
      </header>

      {recado && <Recado tipo={recado.tipo} texto={recado.texto} />}

      <section>
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h3 className="font-display text-base font-bold text-titulo">Os 27 estados</h3>
            <p className="mt-0.5 text-sm text-cinza">
              A <em>pista da planilha</em> é o que a origem afirmava sobre os modelos daquela
              junta — serve para conferir se o catálogo abaixo está completo, e não decide nada
              na tela do aluno.
            </p>
          </div>
          <label className="flex items-center gap-2 text-sm text-tinta">
            <input
              type="checkbox"
              checked={soPorConferir}
              onChange={(e) => setSoPorConferir(e.target.checked)}
              className="size-4 accent-laranja"
            />
            Mostrar só os por conferir
          </label>
        </div>

        <ul className="mt-4 space-y-3">
          {/* A chave de cada linha carrega o que ela mostra: depois de salvar, a
              linha REMONTA com o dado novo do banco, em vez de ficar com o estado
              local de antes do clique. */}
          {visiveis.map((e) => (
            <li key={`${e.uf}:${e.links_conferidos_em ?? ""}:${e.junta}:${e.nome}:${e.integrador}`}>
              <LinhaDeEstado
                estado={e}
                onSalvo={(texto) => {
                  aviso("ok", texto);
                  void recarregar();
                }}
                onErro={(texto) => aviso("erro", texto)}
              />
            </li>
          ))}
          {visiveis.length === 0 && (
            <li className="surface p-5 text-sm text-cinza">
              Todos os {estados.length} estados estão com os links conferidos.
            </li>
          )}
        </ul>
      </section>

      <section>
        <h3 className="font-display text-base font-bold text-titulo">Os modelos de contrato</h3>
        <p className="mt-0.5 max-w-2xl text-sm text-cinza">
          Desligar tira o modelo da tela do aluno sem apagar o arquivo; religar traz de volta.
          O título é o que o aluno lê na lista — o arquivo não muda.
        </p>
        <ListaDeModelosAdmin
          modelos={modelos}
          onSalvo={(texto) => {
            aviso("ok", texto);
            void recarregar();
          }}
          onErro={(texto) => aviso("erro", texto)}
        />
      </section>
    </div>
  );
}

// ---------------------------------------------------------------------------

function LinhaDeEstado({
  estado: e,
  onSalvo,
  onErro,
}: {
  estado: EstadoParaAdmin;
  onSalvo: (texto: string) => void;
  onErro: (texto: string) => void;
}) {
  const [nome, setNome] = useState(e.nome);
  const [junta, setJunta] = useState(e.junta);
  const [integrador, setIntegrador] = useState<Integrador>(e.integrador);
  const [nota, setNota] = useState(e.nota_de_origem ?? "");
  const [abertura, setAbertura] = useState(e.link_abertura);
  const [alteracao, setAlteracao] = useState(e.link_alteracao);
  const [baixa, setBaixa] = useState(e.link_baixa);
  const [ocupado, setOcupado] = useState<"dados" | "links" | null>(null);

  const conferido = Boolean(e.links_conferidos_em);
  const quando = e.links_conferidos_em
    ? new Date(e.links_conferidos_em).toLocaleDateString("pt-BR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      })
    : null;
  const quem = e.conferidor?.nome ?? "a equipe";

  const linksMudaram =
    abertura !== e.link_abertura || alteracao !== e.link_alteracao || baixa !== e.link_baixa;

  async function salvarDados() {
    setOcupado("dados");
    const erro = await salvarEstado(e.uf, {
      nome: nome.trim(),
      junta: junta.trim(),
      integrador,
      nota_de_origem: nota.trim() || null,
    });
    setOcupado(null);
    if (erro) onErro(`${e.uf}: ${erro}`);
    else onSalvo(`${e.uf}: dados salvos.`);
  }

  async function confirmar() {
    setOcupado("links");
    const erro = await confirmarLinks(e.uf, {
      abertura: abertura.trim(),
      alteracao: alteracao.trim(),
      baixa: baixa.trim(),
    });
    setOcupado(null);
    if (erro) onErro(`${e.uf}: ${erro}`);
    else onSalvo(`${e.uf}: links confirmados.`);
  }

  return (
    <section
      role="group"
      aria-labelledby={`estado-${e.uf}`}
      data-uf={e.uf}
      data-conferido={conferido ? "sim" : "nao"}
      className={`surface p-4 sm:p-5 ${conferido ? "" : "ring-1 ring-alerta-tinta/40"}`}
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h4 id={`estado-${e.uf}`} className="font-display text-base font-bold text-titulo">
            {e.nome} <span className="font-mono text-sm font-normal text-cinza">{e.uf}</span>
          </h4>
          <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-cinza">
            <SeloDeIntegrador integrador={e.integrador} />
            <span>{e.junta}</span>
          </div>
        </div>
        <p
          data-selo-conferencia
          className={`rounded-full px-2.5 py-1 text-xs font-semibold ${
            conferido ? "bg-aviso-fundo text-aviso-tinta" : "bg-alerta-fundo text-alerta-tinta"
          }`}
        >
          {conferido ? `Links conferidos em ${quando} por ${quem}` : "Links por conferir"}
        </p>
      </div>

      {e.nota_de_origem && (
        <p className="mt-3 rounded-cf bg-superficie-2 px-3 py-2 text-xs leading-relaxed text-cinza">
          <span className="font-semibold text-tinta">Pista da planilha:</span> {e.nota_de_origem}
        </p>
      )}

      <div className="mt-4 grid gap-3 sm:grid-cols-3">
        <label className="block text-sm">
          <span className="mb-1 block text-xs font-semibold text-cinza">Abrir empresa</span>
          <input
            className={CAMPO}
            value={abertura}
            onChange={(ev) => setAbertura(ev.target.value)}
            inputMode="url"
          />
          <a
            href={e.link_abertura}
            target="_blank"
            rel="noreferrer"
            className="mt-1 inline-block text-xs font-semibold text-link hover:underline"
          >
            Abrir o atual ↗
          </a>
        </label>
        <label className="block text-sm">
          <span className="mb-1 block text-xs font-semibold text-cinza">Alterar empresa</span>
          <input
            className={CAMPO}
            value={alteracao}
            onChange={(ev) => setAlteracao(ev.target.value)}
            inputMode="url"
          />
          <a
            href={e.link_alteracao}
            target="_blank"
            rel="noreferrer"
            className="mt-1 inline-block text-xs font-semibold text-link hover:underline"
          >
            Abrir o atual ↗
          </a>
        </label>
        <label className="block text-sm">
          <span className="mb-1 block text-xs font-semibold text-cinza">Baixar empresa</span>
          <input
            className={CAMPO}
            value={baixa}
            onChange={(ev) => setBaixa(ev.target.value)}
            inputMode="url"
          />
          <a
            href={e.link_baixa}
            target="_blank"
            rel="noreferrer"
            className="mt-1 inline-block text-xs font-semibold text-link hover:underline"
          >
            Abrir o atual ↗
          </a>
        </label>
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={confirmar}
          disabled={ocupado !== null}
          className="pill-cta text-sm disabled:opacity-50"
        >
          {ocupado === "links" ? "Confirmando…" : "Salvar e confirmar links"}
        </button>
        {linksMudaram && (
          <span className="text-xs text-alerta-tinta">
            Há link alterado e não salvo.
          </span>
        )}
      </div>

      <details className="mt-4">
        <summary className="cursor-pointer text-sm font-semibold text-link">
          Dados do estado (nome, junta, sistema, pista)
        </summary>
        <div className="mt-3 grid gap-3 sm:grid-cols-2">
          <label className="block text-sm">
            <span className="mb-1 block text-xs font-semibold text-cinza">Nome</span>
            <input className={CAMPO} value={nome} onChange={(ev) => setNome(ev.target.value)} />
          </label>
          <label className="block text-sm">
            <span className="mb-1 block text-xs font-semibold text-cinza">Junta comercial</span>
            <input className={CAMPO} value={junta} onChange={(ev) => setJunta(ev.target.value)} />
          </label>
          <label className="block text-sm">
            <span className="mb-1 block text-xs font-semibold text-cinza">Sistema integrador</span>
            <select
              className={CAMPO}
              value={integrador}
              onChange={(ev) => setIntegrador(ev.target.value as Integrador)}
            >
              {INTEGRADORES.map((i) => (
                <option key={i.valor} value={i.valor}>
                  {i.rotulo}
                </option>
              ))}
            </select>
          </label>
          <label className="block text-sm">
            <span className="mb-1 block text-xs font-semibold text-cinza">Pista da planilha</span>
            <input className={CAMPO} value={nota} onChange={(ev) => setNota(ev.target.value)} />
          </label>
        </div>
        <button
          type="button"
          onClick={salvarDados}
          disabled={ocupado !== null}
          className="btn-contorno mt-3 text-sm disabled:opacity-50"
        >
          {ocupado === "dados" ? "Salvando…" : "Salvar dados"}
        </button>
        <p className="mt-2 text-xs text-cinza">
          Estes campos não mexem na conferência dos links. Hoje o integrador é{" "}
          {rotuloDoIntegrador(e.integrador)}.
        </p>
      </details>
    </section>
  );
}

// ---------------------------------------------------------------------------

function ListaDeModelosAdmin({
  modelos,
  onSalvo,
  onErro,
}: {
  modelos: ModeloDeContrato[];
  onSalvo: (texto: string) => void;
  onErro: (texto: string) => void;
}) {
  // Agrupa por alcance: os de todos os estados primeiro (casa + DREI), depois
  // cada junta. É a ordem em que a equipe pensa — "o que vale para todo mundo" e
  // "o que é de um estado".
  const grupos = new Map<string, ModeloDeContrato[]>();
  for (const m of modelos) {
    const chave = m.uf ?? "_todos";
    if (!grupos.has(chave)) grupos.set(chave, []);
    grupos.get(chave)!.push(m);
  }
  const ordem = [...grupos.keys()].sort((a, b) =>
    a === "_todos" ? -1 : b === "_todos" ? 1 : a.localeCompare(b),
  );

  return (
    <div className="mt-4 space-y-5">
      {ordem.map((chave) => (
        <div key={chave}>
          <h4 className="mb-2 font-display text-xs font-bold uppercase tracking-widest text-cinza">
            {chave === "_todos" ? "Todos os estados — casa e DREI" : `Junta de ${chave}`}
          </h4>
          <ul className="space-y-2">
            {grupos.get(chave)!.map((m) => (
              <li key={`${m.id}:${m.ativo}:${m.titulo}`}>
                <LinhaDeModelo modelo={m} onSalvo={onSalvo} onErro={onErro} />
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}

function LinhaDeModelo({
  modelo: m,
  onSalvo,
  onErro,
}: {
  modelo: ModeloDeContrato;
  onSalvo: (texto: string) => void;
  onErro: (texto: string) => void;
}) {
  const [titulo, setTitulo] = useState(m.titulo);
  const [ocupado, setOcupado] = useState(false);

  async function alternar() {
    setOcupado(true);
    const erro = await definirModeloAtivo(m.id, !m.ativo);
    setOcupado(false);
    if (erro) onErro(erro);
    else onSalvo(`${m.titulo}: ${m.ativo ? "desligado" : "ligado"}.`);
  }

  async function renomear() {
    setOcupado(true);
    const erro = await renomearModelo(m.id, titulo.trim());
    setOcupado(false);
    if (erro) onErro(erro);
    else onSalvo("Título salvo.");
  }

  return (
    <div
      data-modelo-id={m.id}
      data-ativo={m.ativo ? "sim" : "nao"}
      className={`surface flex flex-wrap items-center gap-3 p-3 ${m.ativo ? "" : "opacity-70"}`}
    >
      <span className="w-28 shrink-0 text-xs text-cinza">
        {rotuloDaCategoria(m.categoria)} · {m.origem === "cf" ? "casa" : m.origem === "drei" ? "DREI" : "junta"}
      </span>
      <input
        className={`${CAMPO} min-w-[14rem] flex-1`}
        value={titulo}
        onChange={(ev) => setTitulo(ev.target.value)}
        aria-label={`Título do modelo ${m.arquivo}`}
      />
      <span className="shrink-0 rounded bg-chip px-1.5 py-0.5 font-mono text-[10px] text-chip-tinta">
        {m.arquivo}
      </span>
      <div className="flex shrink-0 gap-2">
        {titulo.trim() !== m.titulo && (
          <button
            type="button"
            onClick={renomear}
            disabled={ocupado || titulo.trim() === ""}
            className="btn-contorno text-xs disabled:opacity-50"
          >
            Renomear
          </button>
        )}
        <button
          type="button"
          onClick={alternar}
          disabled={ocupado}
          className="btn-contorno text-xs disabled:opacity-50"
        >
          {m.ativo ? "Desligar" : "Ligar"}
        </button>
      </div>
    </div>
  );
}

// As categorias não são usadas diretamente aqui, mas a lista de rótulos é a
// mesma da tela do aluno — importar garante que renomear uma categoria lá
// quebre a compilação aqui, e não deixe dois vocabulários.
void CATEGORIAS;
